import { Requisition, RequisitionItem } from '@kalos/kalos-rpc';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { InventoryClientService } from '../../tools/helpers';
import { queryKeys } from './constants';
import { getFileQueryConfig } from './useFileQuery';
import { type EntityFilter } from './utils';

// Helper function to parse comma-delimited file IDs
const parseFileIds = (fileIdString: string | undefined): number[] => {
  if (!fileIdString) return [];
  return fileIdString
    .split(',')
    .map((id) => parseInt(id.trim()))
    .filter((id) => !isNaN(id));
};

export type RequisitionFilter = EntityFilter<Requisition>;

export const getRequisitionQueryKeys = (filter: RequisitionFilter) => [
  queryKeys.requisition.root,
  queryKeys.requisition.getHash(filter),
];

export const useRequisitionQuery = ({
  filters = {},
  enabled = true,
}: {
  filters?: RequisitionFilter;
  enabled?: boolean;
}) => {
  return useQuery({
    queryKey: getRequisitionQueryKeys(filters),
    queryFn: () => {
      const req = Requisition.create({ ...filters, overrideCreatedBy: true });
      return InventoryClientService.GetRequisition(req);
    },
    enabled,
  });
};

export const useBatchGetRequisitionQuery = ({
  filters = {},
  enabled = true,
  fetchAllItems = false, // New parameter to enable fetching all items
}: {
  filters?: RequisitionFilter;
  enabled?: boolean;
  fetchAllItems?: boolean;
}) => {
  return useQuery({
    // eslint-disable-next-line @tanstack/query/exhaustive-deps
    queryKey: [
      queryKeys.requisition.root,
      queryKeys.requisition.list,
      queryKeys.requisition.getHash(filters),
      fetchAllItems ? 'all-items' : 'default',
    ],
    queryFn: async () => {
      const response = await InventoryClientService.BatchGetRequisition(
        Requisition.create({ ...filters, overrideCreatedBy: true }),
      );

      // If fetchAllItems is true, fetch all items for each requisition using withoutLimit
      if (fetchAllItems && response.results) {
        const enrichedResults = await Promise.all(
          response.results.map(async (requisition) => {
            if (requisition.id) {
              // Fetch all items for this requisition in a single request using withoutLimit
              const itemsResponse = await InventoryClientService.BatchGetRequisitionItem(
                RequisitionItem.create({
                  requisitionId: requisition.id,
                  withoutLimit: true, // Bypass pagination to get all items
                } as any),
              );

              const allItems = itemsResponse.results || [];

              // Return requisition with all items
              return { ...requisition, items: allItems };
            }
            return requisition;
          }),
        );

        return { ...response, results: enrichedResults };
      }

      return response;
    },
    enabled,
  });
};

export const useRequisitionCreateMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (requisition: Requisition) => {
      return await InventoryClientService.CreateRequisition(requisition);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [queryKeys.requisition.root] });
      // A new request changes the balance available (pending requests count against it), so
      // the balance shown afterwards isn't left stale.
      queryClient.invalidateQueries({ queryKey: [queryKeys.toolFundBalance.root] });
    },
  });
};

export const useRequisitionUpdateMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (requisition: Requisition) => {
      return await InventoryClientService.UpdateRequisition(requisition);
    },
    onSuccess: async (_, updateRequest) => {
      const updatePromises: Promise<void>[] = [];
      // Invalidate all requisition queries (both list and individual)
      updatePromises.push(
        queryClient.invalidateQueries({
          queryKey: [queryKeys.requisition.root],
        }),
      );
      if (updateRequest.fieldMask.includes('FileIds') && updateRequest.fileIds) {
        // Parse comma-delimited file IDs and invalidate queries for each file
        const fileIds = parseFileIds(updateRequest.fileIds);
        fileIds.forEach((fileId) => {
          updatePromises.push(
            queryClient.invalidateQueries({
              queryKey: getFileQueryConfig({ filter: { id: fileId } }).queryKey,
            }),
          );
        });
      }

      await Promise.allSettled(updatePromises);
    },
  });
};

export const useRequisitionDeleteMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (requisition: Requisition) => {
      return await InventoryClientService.DeleteRequisition(requisition);
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: [queryKeys.requisition.root] });
    },
  });
};

export const useToolFundTransferMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (params: { assetId: number; newUserId: number }) => {
      const req = Requisition.create({
        id: params.assetId,
        userId: params.newUserId,
        updatedAt: new Date().toISOString(),
        fieldMask: ['user_id', 'updated_at'],
      });
      return await InventoryClientService.UpdateRequisition(req);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [queryKeys.requisition.root] });
    },
  });
};

export type RequisitionItemFilter = EntityFilter<RequisitionItem>;

export const getRequisitionItemQueryKeys = (filter: RequisitionItemFilter) => [
  queryKeys.requisition.root,
  'items',
  queryKeys.requisition.getHash(filter),
];

export const useBatchGetRequisitionItemQuery = ({
  filters = {},
  enabled = true,
}: {
  filters?: RequisitionItemFilter;
  enabled?: boolean;
}) => {
  return useQuery({
    queryKey: getRequisitionItemQueryKeys(filters),
    queryFn: async () => {
      const response = await InventoryClientService.BatchGetRequisitionItem(
        RequisitionItem.create(filters),
      );
      return response;
    },
    enabled,
  });
};

/**
 * Fetches ALL requisition items for a given requisition ID using withoutLimit flag.
 * This solves the issue where only 25 items are returned by default.
 */
export const useAllRequisitionItemsQuery = ({
  requisitionId,
  enabled = true,
}: {
  requisitionId: number;
  enabled?: boolean;
}) => {
  return useQuery({
    queryKey: getRequisitionItemQueryKeys({ requisitionId, withoutLimit: true }),
    queryFn: async () => {
      const response = await InventoryClientService.BatchGetRequisitionItem(
        RequisitionItem.create({
          requisitionId,
          withoutLimit: true, // Bypass pagination to get all items
        } as any),
      );

      return { results: response.results || [], totalCount: response.results?.length || 0 };
    },
    enabled: enabled && requisitionId > 0,
  });
};
